// The time-zone picker (time-zone PR): a search box over one list — "Automatic (this
// device)", the six US zones, then every zone the browser knows, sorted by offset.
// Used by Settings → Account → Time zone and by the admin user page. Keyboard: type to
// search, ↑ / ↓ to move, Enter to pick, Escape to close without changing anything.

import { useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { deviceZone, searchZones, zoneOptions, zoneTitle, type ZoneOption } from '../time';

const AUTO = '__auto__';
const AUTO_WORDS = 'automatic auto this device browser computer phone';

type Row = { kind: 'head'; text: string } | { kind: 'option'; id: string; title: string; sub: string; offset: string };

export function TimeZonePicker({
  value,
  onPick,
  onCancel,
  busy = false,
  automaticSub,
  label = 'Time zone',
}: {
  /** the saved zone; null = Automatic */
  value: string | null;
  onPick: (zone: string | null) => void;
  onCancel: () => void;
  busy?: boolean;
  /** second line of the Automatic option ("Eastern Time on this device", "their device: Karachi") */
  automaticSub?: string;
  label?: string;
}) {
  const uid = useId();
  const listId = `${uid}-list`;
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const options = useMemo(() => zoneOptions(), []);

  const rows: Row[] = useMemo(() => {
    const opt = (o: ZoneOption): Row => ({ kind: 'option', id: o.id, title: o.title, sub: o.sub, offset: o.offsetText });
    const auto: Row = { kind: 'option', id: AUTO, title: 'Automatic (this device)', sub: automaticSub ?? `${zoneTitle(deviceZone())} on this device`, offset: '' };
    const q = query.trim();
    if (!q) {
      return [auto, { kind: 'head', text: 'Common in the US' }, ...options.pinned.map(opt), { kind: 'head', text: 'All time zones' }, ...options.all.map(opt)];
    }
    const words = q.toLowerCase().split(/\s+/);
    const autoHit = words.every((w) => AUTO_WORDS.includes(w));
    const hits = [...searchZones(options.pinned, q), ...searchZones(options.all, q)].map(opt);
    return [...(autoHit ? [auto] : []), ...hits];
  }, [options, query, automaticSub]);

  const picks = rows.filter((r): r is Extract<Row, { kind: 'option' }> => r.kind === 'option');
  const activeIdx = Math.min(active, Math.max(picks.length - 1, 0));
  const current = value ?? AUTO;

  const choose = (id: string) => {
    if (busy) return;
    if (id === current) onCancel();
    else onPick(id === AUTO ? null : id);
  };

  const move = (to: number) => {
    const next = Math.max(0, Math.min(picks.length - 1, to));
    setActive(next);
    const el = listRef.current?.querySelector<HTMLElement>(`[data-i="${next}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      move(activeIdx + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      move(activeIdx - 1);
    } else if (e.key === 'PageDown') {
      e.preventDefault();
      move(activeIdx + 8);
    } else if (e.key === 'PageUp') {
      e.preventDefault();
      move(activeIdx - 8);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const p = picks[activeIdx];
      if (p) choose(p.id);
    } else if (e.key === 'Escape') {
      // close the picker only, not the Settings dialog around it
      e.preventDefault();
      e.stopPropagation();
      onCancel();
    }
  };

  let i = -1;
  return (
    <div className="tz-picker">
      <label className="sr-only" htmlFor={`${uid}-q`}>
        Search time zones
      </label>
      <input
        id={`${uid}-q`}
        className="tz-search"
        type="search"
        autoFocus
        autoComplete="off"
        spellCheck={false}
        placeholder="Search city, country or offset (+5, PST)"
        role="combobox"
        aria-expanded="true"
        aria-controls={listId}
        aria-activedescendant={picks[activeIdx] ? `${uid}-o-${activeIdx}` : undefined}
        value={query}
        disabled={busy}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
          listRef.current?.scrollTo({ top: 0 });
        }}
        onKeyDown={onKey}
      />
      <ul id={listId} ref={listRef} className="tz-list" role="listbox" aria-label={label} aria-busy={busy}>
        {rows.map((r) => {
          if (r.kind === 'head') {
            return (
              <li key={`h-${r.text}`} className="tz-head" role="presentation">
                {r.text}
              </li>
            );
          }
          i += 1;
          const n = i;
          const selected = r.id === current;
          return (
            <li
              key={r.id}
              id={`${uid}-o-${n}`}
              data-i={n}
              role="option"
              aria-selected={selected}
              className={`tz-option${n === activeIdx ? ' tz-active' : ''}${selected ? ' tz-current' : ''}`}
              onMouseMove={() => n !== activeIdx && setActive(n)}
              onClick={() => choose(r.id)}
            >
              <span className="tz-text">
                <span className="tz-title">{r.title}</span>
                {r.sub && <span className="tz-sub">{r.sub}</span>}
              </span>
              {r.offset && <span className="tz-offset">{r.offset}</span>}
              <span className="tz-check" aria-hidden>
                {selected ? '✓' : ''}
              </span>
            </li>
          );
        })}
        {!picks.length && (
          <li className="tz-none" role="presentation">
            No time zone matches “{query.trim()}”. Try a city or an offset like +5.
          </li>
        )}
      </ul>
      <div className="tz-actions">
        <button className="btn btn-small btn-quiet" type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}
